import { WebhookConfig } from '@/pages/Config/Webhooks/types';
import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse } from '../request.types';
import {
    GetWebhookPayload,
    GetWebhooksPayload,
    PostWebhookPayload,
    PutWebhookPayload,
    DeleteWebhookPayload,
    WebhookResponseData,
} from './moduleExternalWebhooks.type';

const moduleExternalWebhooksApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getWebhook: build.query<WebhookConfig, ApiRequest<GetWebhookPayload>>({
            query: ({ token, webhookId }) => ({
                url: `/module_external_webhooks/${webhookId}`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<WebhookResponseData>): WebhookConfig => {
                const { data } = response;
                return {
                    ...data,
                    additionalHeaders: JSON.parse(data.additionalHeaders),
                    eventTypes: data.eventType ? data.eventType.split(',') : [],
                };
            },
            providesTags: (result, error, { webhookId }) => [{ type: ApiTagTypes.Webhook, id: webhookId }],
            extraOptions: { maxRetries: 3 },
        }),
        getWebhooks: build.query<WebhookResponseData[], ApiRequest<GetWebhooksPayload>>({
            query: ({ token, brandId, restaurantId }) => ({
                url: `/module_external_webhooks`,
                method: 'GET',
                params: {
                    brandId,
                    ...(restaurantId != null && { restaurantId }),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<WebhookResponseData[]>): WebhookResponseData[] => {
                return response.data;
            },
            providesTags: (result, error, { brandId, restaurantId }) => [
                { type: ApiTagTypes.Webhook, id: 'LIST' },
                { type: ApiTagTypes.Webhook, id: `BRAND_${brandId}` },
                ...(restaurantId != null ? [{ type: ApiTagTypes.Webhook, id: `RESTAURANT_${restaurantId}` }] : []),
            ],
            extraOptions: { maxRetries: 3 },
        }),
        postWebhook: build.mutation<number, ApiRequest<PostWebhookPayload>>({
            query: ({ token, body }) => ({
                url: `/module_external_webhooks/`,
                method: 'POST',
                body,
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<WebhookResponseData>): number => {
                const { data } = response;
                return data.moduleExternalWebhookId;
            },
            invalidatesTags: (result, error, { body }) => [
                { type: ApiTagTypes.Webhook, id: 'LIST' },
                { type: ApiTagTypes.Webhook, id: `BRAND_${body.brandId}` },
                ...(body.restaurantId != null
                    ? [{ type: ApiTagTypes.Webhook, id: `RESTAURANT_${body.restaurantId}` }]
                    : []),
            ],
            extraOptions: { maxRetries: 3 },
        }),
        putWebhook: build.mutation<WebhookConfig, ApiRequest<PutWebhookPayload>>({
            query: ({ token, webhookId, body }) => ({
                url: `/module_external_webhooks/${webhookId}`,
                method: 'PUT',
                body,
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<WebhookResponseData>): WebhookConfig => {
                const { data } = response;
                return {
                    ...data,
                    additionalHeaders: JSON.parse(data.additionalHeaders),
                    eventTypes: data.eventType ? data.eventType.split(',') : [],
                };
            },
            invalidatesTags: (result, error, { webhookId, body }) => [
                { type: ApiTagTypes.Webhook, id: webhookId },
                { type: ApiTagTypes.Webhook, id: 'LIST' },
                { type: ApiTagTypes.Webhook, id: `BRAND_${body.brandId}` },
                ...(body.restaurantId != null
                    ? [{ type: ApiTagTypes.Webhook, id: `RESTAURANT_${body.restaurantId}` }]
                    : []),
            ],
            extraOptions: { maxRetries: 3 },
        }),
        deleteWebhook: build.mutation<boolean, ApiRequest<DeleteWebhookPayload>>({
            query: ({ token, webhookId }) => ({
                url: `/module_external_webhooks/${webhookId}`,
                method: 'DELETE',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<WebhookResponseData>): boolean => {
                const { status } = response;
                if (status === 200) {
                    return true;
                }
                return false;
            },
            invalidatesTags: (result, error, { webhookId }) => [
                { type: ApiTagTypes.Webhook, id: webhookId },
                { type: ApiTagTypes.Webhook, id: 'LIST' },
            ],
            extraOptions: { maxRetries: 3 },
        }),
    }),
});

export const {
    useGetWebhookQuery,
    useGetWebhooksQuery,
    usePutWebhookMutation,
    usePostWebhookMutation,
    useDeleteWebhookMutation,
} = moduleExternalWebhooksApi;
